import React, { useState } from "react";

// @material-ui/core
import withStyles from "@material-ui/core/styles/withStyles";
import Slide from "@material-ui/core/Slide";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";

// @material-ui/icons
import Close from "@material-ui/icons/Close";
// core components
import Button from "components/CustomButtons/Button";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
// custom components
import CustomDropdownAdapter from "customComponents/CustomDropdownAdapter/CustomDropdownAdapter";
import CustomDatetimeAdapter from "../components/CustomDatetimeAdapter";
import CustomInputAdapter from "../components/CustomInputAdapter";
import SwitchAdapter from "../components/SwitchAdapter";

import modalStyle from "assets/jss/material-kit-pro-react/modalStyle";
import PropTypes from "prop-types";
import { Form, FormSpy } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { FieldArray } from "react-final-form-arrays";
import createDecorator from "final-form-calculate";

import categories from "../../../utils/categories";

import {
	getCategoryImageURI,
	getSafeFullName,
	mapAbbrevToDayEnums,
	mapAgeGroupEnums,
	mapAgeGroupToEnums,
	mapBillingIntervalEnums,
	mapBillingIntervalToEnums,
	mapDayEnumsToAbbrev,
} from "../../../utils/Utils";
import LocationModal from "../LocationModal";
import ContactModal from "../ContactModal";
import FieldAdapter from "../components/FieldAdapter";
import ImageSelectAdapter from "../components/ImageSelectAdapter";
import EditorAdapter from "../components/EditorAdapter";

const genderDecorator = createDecorator(
	{
		field: "targetGroup.male",
		updates: {
			"targetGroup.female": (male, all) =>
				male ? all.targetGroup.female : true,
		},
	},
	{
		field: "targetGroup.female",
		updates: {
			"targetGroup.male": (female, all) =>
				female ? all.targetGroup.male : true,
		},
	},
);

const style = (theme) => ({
	...modalStyle(theme),
	modalRootExample: {
		"& > div:first-child": {
			display: "none",
		},
		backgroundColor: "rgba(0, 0, 0, 0.5)",
	},
	removeButton: {
		display: "flex",
		justifyContent: "flex-end",
	},
});

// eslint-disable-next-line react/display-name
const Transition = React.forwardRef((props, ref) => (
	<Slide direction="down" ref={ref} {...props} />
));

function mapEnumsToDisplay(course) {
	const schedules = course.schedule.schedules.map((schedule) => ({
		...schedule,
		day: mapDayEnumsToAbbrev(schedule.day),
	}));
	const costs = course.costs
		? course.costs.map((cost) => ({
				ageGroup: mapAgeGroupEnums(cost.ageGroup),
				cost: cost.cost,
				billingInterval: mapBillingIntervalEnums(cost.billingInterval),
		  }))
		: [{}];
	return {
		...course,
		schedule: {
			...course.schedule,
			schedules,
		},
		costs,
	};
}

function mapDisplayToEnums(course) {
	const schedules = course.schedule.schedules.map((schedule) => ({
		...schedule,
		day: mapAbbrevToDayEnums(schedule.day),
	}));
	const costs = course.costs
		? course.costs.map((cost) => ({
				ageGroup: mapAgeGroupToEnums(cost.ageGroup),
				cost: cost.cost,
				billingInterval: mapBillingIntervalToEnums(cost.billingInterval),
		  }))
		: [{}];
	return {
		...course,
		schedule: {
			...course.schedule,
			schedules,
		},
		costs,
	};
}

const CourseModal = (props) => {
	const [open, setOpen] = useState(false);
	const [course, setCourse] = useState();
	const [imageURI, setImageURI] = useState();

	const preProcessCourse = async (course) => {
		const categoryImageURI = getCategoryImageURI(course.category);
		setImageURI(course.image ? course.image : categoryImageURI);

		return {
			...mapEnumsToDisplay(course),
			description: course.descriptionAsHtml,
			imageFile: null,
		};
	};

	const onSubmit = async (values) => {
		const { imageFile, ...course } = mapDisplayToEnums(values);
		props.onSubmit(course, imageFile);
		handleClose();
	};

	const handleClickOpen = async () => {
		const preProcessedCourse = await preProcessCourse(props.course);
		setCourse({
			...preProcessedCourse,
			event: props.events.find(
				(event) => event.id === preProcessedCourse.eventID,
			),
		});
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const {
		classes,
		events,
		contacts,
		locations,
		createContact,
		createLocation,
		button,
	} = props;

	return (
		<div>
			<div onClick={() => handleClickOpen()}>{button}</div>
			{open && course && (
				<Dialog
					classes={{
						root: `${classes.modalRoot} ${classes.modalRootExample}`,
						paper: `${classes.modal} ${classes.modalLarge}`,
					}}
					open={open}
					TransitionComponent={Transition}
					keepMounted
					onClose={() => handleClose("scrollingModal")}
					aria-labelledby="classic-modal-slide-title"
					aria-describedby="classic-modal-slide-description"
				>
					<Form
						onSubmit={onSubmit}
						decorators={[genderDecorator]}
						initialValues={course}
						mutators={{ ...arrayMutators }}
						render={({
							handleSubmit,
							form,
							submitting,
							pristine,
							values,
							form: {
								mutators: { push },
							},
						}) => (
							<form onSubmit={handleSubmit}>
								<DialogTitle
									id="classic-modal-slide-title"
									disableTypography
									className={classes.modalHeader}
								>
									<Button
										simple
										className={classes.modalCloseButton}
										key="close"
										aria-label="Close"
										onClick={() => handleClose("scrollingModal")}
									>
										{" "}
										<Close className={classes.modalClose} />
									</Button>
									<h4 className={classes.modalTitle}>
										{values.title ? values.title : "Neuer Kurs"}
									</h4>
								</DialogTitle>
								<DialogContent
									id="classic-modal-slide-description"
									className={classes.modalBody}
								>
									<GridContainer>
										<GridItem>
											<FieldAdapter
												name="imageFile"
												component={ImageSelectAdapter}
												type="file"
												id="imageFile"
												imagePath={imageURI}
											/>
										</GridItem>
										<FormSpy
											subscription={{ modified: true, values: true }}
											onChange={({ modified, values }) => {
												if (modified.category) {
													const categoryImageURI = getCategoryImageURI(
														values.category,
													);
													setImageURI(
														values.image ? values.image : categoryImageURI,
													);
												}
											}}
										/>
										<GridItem>
											<FieldAdapter
												name="category"
												component={CustomDropdownAdapter}
												type="text"
												placeholder="Sportart"
												label="Sportart"
												dropDownList={categories}
												id="category"
												nonReactInputProps={{
													required: true,
												}}
												validate={(value) => !value && "Eingabe erforderlich"}
											/>
										</GridItem>
										<GridItem>
											<FieldAdapter
												name="title"
												component={CustomInputAdapter}
												type="text"
												placeholder="Titel"
												label="Titel"
												nonReactInputProps={{
													required: true,
												}}
												validate={(value) => !value && "Eingabe erforderlich"}
											/>
										</GridItem>
										<GridItem>
											<FieldAdapter
												name="description"
												component={EditorAdapter}
												label="Beschreibung"
												id="description"
											/>
										</GridItem>
										<GridItem>
											<FieldAdapter
												name="district"
												component={CustomDropdownAdapter}
												type="text"
												label="Stadtteil"
												dropDownList={districts}
												id="district"
												inputProps={{ required: true }}
												validate={(value) => !value && "Eingabe erforderlich"}
											/>
										</GridItem>
										<GridItem>
											<FieldAdapter
												name="event"
												component={CustomDropdownAdapter}
												type="text"
												placeholder="Veranstaltung"
												label="Veranstaltung"
												dropDownList={events}
												id="event"
												getKey={(item) => item.id}
												getValue={(item) => item}
												getName={(item) => item.name}
											/>
										</GridItem>
										<GridItem xs={12} sm={6} md={6} lg={6}>
											<h4>Trainingszeiten</h4>
										</GridItem>
										<GridItem xs={12} sm={6} md={6} lg={6}>
											<Button
												color="primary"
												simple
												onClick={() => push("schedule.schedules", undefined)}
											>
												<i className="fas fa-plus" /> Neue Trainingszeit
											</Button>
										</GridItem>
										<GridItem xs={12}>
											<GridContainer>
												<GridItem xs={3} sm={3} md={3} lg={3}>
													<FieldAdapter
														name={"schedule.startingDate"}
														component={CustomDatetimeAdapter}
														label="Start"
														dateFormat={"YYYY-MM-DD"}
														timeFormat={false}
														validate={(value) => {
															const match =
																/^[0-9][0-9][0-9][0-9]-([1][0-2]|[0][1-9])-[0-3][0-9]$/;
															const matchesPattern = match.test(value);
															if (!value) {
																return "Eingabe erforderlich";
															} else if (!matchesPattern) {
																return "Datum muss von Format YYYY-MM-DD sein";
															}
														}}
													/>
												</GridItem>
												<GridItem xs={3} sm={3} md={3} lg={3}>
													<FieldAdapter
														name={"schedule.finalDate"}
														component={CustomDatetimeAdapter}
														label="Ende"
														dateFormat={"YYYY-MM-DD"}
														timeFormat={false}
														validate={(value) => {
															const match =
																/^[0-9][0-9][0-9][0-9]-([1][0-2]|[0][1-9])-[0-3][0-9]$/;
															const matchesPattern = match.test(value);
															if (!value) {
																return "Eingabe erforderlich";
															} else if (!matchesPattern) {
																return "Datum muss von Format YYYY-MM-DD sein";
															}
														}}
													/>
												</GridItem>
											</GridContainer>
										</GridItem>
										<GridItem xs={12}>
											<FieldArray name="schedule.schedules">
												{({ fields }) =>
													fields.map((name, index) => (
														<GridContainer key={name}>
															<GridItem xs={3} sm={3} md={3} lg={3}>
																<FieldAdapter
																	name={`${name}.day`}
																	placeholder="Tag"
																	component={CustomDropdownAdapter}
																	type="text"
																	label="Tag"
																	dropDownList={[
																		"Mo",
																		"Di",
																		"Mi",
																		"Do",
																		"Fr",
																		"Sa",
																		"So",
																		"Jeden Tag",
																	]}
																	id={`${name}.day`}
																	validate={(value) =>
																		!value && "Eingabe erforderlich"
																	}
																/>
															</GridItem>
															<GridItem xs={3} sm={3} md={3} lg={3}>
																<FieldAdapter
																	name={`${name}.from`}
																	component={CustomDatetimeAdapter}
																	label="Von"
																	dateFormat={false}
																	timeFormat={"HH:mm"}
																	validate={(value) => {
																		const match =
																			/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
																		const matchesPattern = match.test(value);
																		if (!value) {
																			return "Eingabe erforderlich";
																		} else if (!matchesPattern) {
																			return "Zeit muss von Format HH:MM sein";
																		}
																	}}
																/>
															</GridItem>
															<GridItem xs={3} sm={3} md={3} lg={3}>
																<FieldAdapter
																	name={`${name}.to`}
																	component={CustomDatetimeAdapter}
																	label="Bis"
																	dateFormat={false}
																	timeFormat={"HH:mm"}
																	validate={(value) => {
																		const match =
																			/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
																		const matchesPattern = match.test(value);
																		if (!value) {
																			return "Eingabe erforderlich";
																		} else if (!matchesPattern) {
																			return "Zeit muss von Format HH:MM sein";
																		}
																	}}
																/>
															</GridItem>
															<GridItem
																xs={3}
																sm={3}
																md={3}
																lg={3}
																className={classes.removeButton}
															>
																{fields.length !== 1 && (
																	<Button
																		color="danger"
																		simple
																		onClick={() => fields.remove(index)}
																	>
																		<i className="fas fa-minus-circle" />
																	</Button>
																)}
															</GridItem>
														</GridContainer>
													))
												}
											</FieldArray>
										</GridItem>
										<GridItem xs={12} sm={6} md={6} lg={6}>
											<h4>Zielgruppe</h4>
										</GridItem>
										<GridItem xs={12}>
											<GridContainer key="targetGroup">
												<GridItem xs={6} sm={6} md={6} lg={6}>
													<FieldAdapter
														name="targetGroup.from"
														component={CustomInputAdapter}
														inputProps={{
															type: "number",
														}}
														placeholder="Von"
														label="Mindestalter"
														validate={(value) => {
															if (value === null || value < 0)
																return "Ungültiges Alter";
														}}
													/>
												</GridItem>

												<GridItem xs={6} sm={6} md={6} lg={6}>
													<FieldAdapter
														name="targetGroup.to"
														component={CustomInputAdapter}
														inputProps={{
															type: "number",
														}}
														placeholder="Bis"
														label="Höchstalter"
														validate={(value) => {
															if (value === null || value < 0)
																return "Ungültiges Alter";
														}}
													/>
												</GridItem>

												<GridItem xs={6} sm={3} md={3} lg={3}>
													<FieldAdapter
														name="targetGroup.female"
														component={SwitchAdapter}
														type="text"
														label="Weiblich"
													/>
												</GridItem>
												<GridItem xs={6} sm={3} md={3} lg={3}>
													<FieldAdapter
														name="targetGroup.male"
														component={SwitchAdapter}
														type="text"
														label="Männlich"
													/>
												</GridItem>
											</GridContainer>
										</GridItem>

										<GridItem xs={12} sm={6} md={6} lg={6}>
											<h4>Gebühren</h4>
										</GridItem>
										<GridItem xs={12} sm={6} md={6} lg={6}>
											<Button
												color="primary"
												simple
												onClick={() => push("costs", undefined)}
											>
												<i className="fas fa-plus" /> Gebühren hinzufügen
											</Button>
										</GridItem>
										<GridItem xs={12}>
											<FieldArray name="costs">
												{({ fields }) =>
													fields.map((name, index) => (
														<GridContainer key={name}>
															<GridItem xs={3} sm={3} md={3} lg={3}>
																<FieldAdapter
																	name={`${name}.billingInterval`}
																	placeholder="Zeitraum"
																	component={CustomDropdownAdapter}
																	type="text"
																	label="Zeitraum"
																	dropDownList={[
																		"Monat",
																		"Halbjahr",
																		"Jahr",
																		"Kurs",
																	]}
																	id={`${name}.billingInterval`}
																/>
															</GridItem>
															<GridItem xs={3} sm={3} md={3} lg={3}>
																<FieldAdapter
																	name={`${name}.cost`}
																	component={CustomInputAdapter}
																	type="text"
																	placeholder="Gebühren"
																	label="Gebühren"
																	nonReactInputProps={{
																		required: true,
																	}}
																/>
															</GridItem>
															<GridItem xs={3} sm={3} md={3} lg={3}>
																<FieldAdapter
																	name={`${name}.ageGroup`}
																	placeholder="Altersklasse"
																	component={CustomDropdownAdapter}
																	type="text"
																	label="Altersklasse"
																	dropDownList={[
																		"Erwachsene",
																		"Kinder",
																		"Senioren",
																		"Studenten",
																		"Jeden",
																	]}
																	id={`${name}.ageGroup`}
																/>
															</GridItem>
															<GridItem
																xs={3}
																sm={3}
																md={3}
																lg={3}
																className={classes.removeButton}
															>
																{fields.length !== 1 && (
																	<Button
																		color="danger"
																		simple
																		onClick={() => fields.remove(index)}
																	>
																		<i className="fas fa-minus-circle" />
																	</Button>
																)}
															</GridItem>
														</GridContainer>
													))
												}
											</FieldArray>
										</GridItem>
										<GridItem>
											<FieldAdapter
												name="hints"
												component={CustomInputAdapter}
												type="text"
												placeholder="Zusatzhinweise"
												label="Zusatzhinweise"
												inputProps={{ multiline: true, maxRows: 10 }}
											/>
										</GridItem>

										<GridItem xs={12} sm={6} md={6} lg={6}>
											<h4>Registrierung</h4>
										</GridItem>
										<GridItem xs={12}>
											<GridContainer>
												<GridItem xs={3} sm={3} md={3} lg={3}>
													<FieldAdapter
														name={"registration.openingDate"}
														component={CustomDatetimeAdapter}
														label="Anmeldebeginn"
														dateFormat={"YYYY-MM-DD"}
														timeFormat={false}
														validate={(value) => {
															if (value) {
																const match =
																	/^[0-9][0-9][0-9][0-9]-([1][0-2]|[0][1-9])-[0-3][0-9]$/;
																const matchesPattern = match.test(value);
																if (!matchesPattern) {
																	return "Datum muss von Format YYYY-MM-DD sein";
																}
															}
														}}
													/>
												</GridItem>
												<GridItem xs={3} sm={3} md={3} lg={3}>
													<FieldAdapter
														name={"registration.closingDate"}
														component={CustomDatetimeAdapter}
														label="Anmeldeschluss"
														dateFormat={"YYYY-MM-DD"}
														timeFormat={false}
														validate={(value) => {
															if (value) {
																const match =
																	/^[0-9][0-9][0-9][0-9]-([1][0-2]|[0][1-9])-[0-3][0-9]$/;
																const matchesPattern = match.test(value);
																if (!matchesPattern) {
																	return "Datum muss von Format YYYY-MM-DD sein";
																}
															}
														}}
													/>
												</GridItem>
												<GridItem xs={6} sm={6} md={6} lg={6}>
													<FieldAdapter
														name="registration.participantLimit"
														component={CustomInputAdapter}
														inputProps={{
															type: "number",
														}}
														parse={(value) => (value ? value : null)}
														label="Verfügbare Plätze"
														validate={(value) => {
															if (value && value < 1)
																return "Es muss mindestens einen Platz geben";
														}}
													/>
												</GridItem>
											</GridContainer>
										</GridItem>
										<GridItem>
											<FieldAdapter
												name="registration.registrationLink"
												component={CustomInputAdapter}
												type="text"
												placeholder="Registrierungslink"
												label="Registrierungslink"
												inputProps={{ multiline: true, maxRows: 10 }}
											/>
										</GridItem>

										<GridItem xs={12} sm={6} md={6} lg={6}>
											<h4>Kontakt</h4>
										</GridItem>
										<GridItem xs={12} sm={6} md={6} lg={6}>
											<ContactModal
												newContact
												createContact={createContact}
												button={(onClick) => {
													return (
														<Button
															color="primary"
															simple
															onClick={() => onClick()}
														>
															<i className="fas fa-plus" /> Neuer Kontakt
														</Button>
													);
												}}
											/>
										</GridItem>
										<GridItem>
											<FieldAdapter
												name="contact"
												component={CustomDropdownAdapter}
												type="text"
												dropDownList={contacts
													.filter((item) => getSafeFullName(item))
													.sort((a, b) =>
														getSafeFullName(a).localeCompare(
															getSafeFullName(b),
														),
													)}
												id="contact"
												getKey={(item) => item.id}
												getValue={(item) => item}
												getName={(item) => `${item.firstName} ${item.lastName}`}
												nonReactInputProps={{
													required: true,
												}}
											/>
										</GridItem>

										<GridItem xs={12} sm={6} md={6} lg={6}>
											<h4>Sportstätte</h4>
										</GridItem>
										<GridItem xs={12} sm={6} md={6} lg={6}>
											<LocationModal
												newLocation
												createLocation={createLocation}
												button={(onClick) => {
													return (
														<Button
															color="primary"
															simple
															onClick={() => onClick()}
														>
															<i className="fas fa-plus" /> Neue Sportstätte
														</Button>
													);
												}}
											/>
										</GridItem>
										<GridItem>
											<FieldAdapter
												name="location"
												component={CustomDropdownAdapter}
												type="text"
												dropDownList={locations
													.filter((item) => item.name)
													.sort((a, b) => a.name.localeCompare(b.name))}
												id="location"
												getKey={(item) => item.id}
												getValue={(item) => item}
												getName={(item) => item.name}
												nonReactInputProps={{
													required: true,
												}}
											/>
										</GridItem>
									</GridContainer>
								</DialogContent>
								<DialogActions className={classes.modalFooter}>
									<Button
										onClick={form.reset}
										disabled={submitting || pristine}
										color="secondary"
									>
										Zurücksetzen
									</Button>
									<Button color="primary" type="submit" disabled={submitting}>
										Speichern
									</Button>
								</DialogActions>
							</form>
						)}
					/>
				</Dialog>
			)}
		</div>
	);
};

CourseModal.defaultProps = {
	course: {
		schedule: {
			schedules: [{}],
		},
		costs: [{}],
		targetGroup: {
			male: true,
			female: true,
			from: 0,
			to: 99,
		},
	},
};

CourseModal.propTypes = {
	classes: PropTypes.object,
	course: PropTypes.object,
	onSubmit: PropTypes.func,
	createContact: PropTypes.func,
	createLocation: PropTypes.func,
	events: PropTypes.array,
	contacts: PropTypes.array,
	locations: PropTypes.array,
	button: PropTypes.node,
};

const districts = [
	"Höhscheid",
	"Mitte",
	"Ohligs",
	"Merscheid",
	"Wald",
	"Burg",
	"Gräfrath",
	"Aufderhöhe",
	"Außerhalb von SG",
];

export default withStyles(style)(CourseModal);
